import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { BarChart3, TrendingDown, MessageSquare, PoundSterling, Users, UserPlus, Layers, Download, Calendar, Building2, Gift } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fmtDay = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; } };
const PLAN_LABEL = { free: "Free", growth: "Growth", scale: "Scale" };
const isoDate = (d) => d.toISOString().slice(0, 10);

const KpiCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-semibold">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <p className="font-display text-3xl font-bold text-[#1E3A5F] mt-2">{value}</p>
    {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
  </div>
);

const PRESETS = [
  { key: "7", label: "7 days", days: 7 },
  { key: "30", label: "30 days", days: 30 },
  { key: "90", label: "90 days", days: 90 },
  { key: "all", label: "All time", days: null },
];

const Insights = () => {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [err, setErr] = useState(false);
  const [preset, setPreset] = useState("30");
  const [fromDate, setFromDate] = useState(() => isoDate(new Date(Date.now() - 30 * 864e5)));
  const [toDate, setToDate] = useState(() => isoDate(new Date()));
  const [downloading, setDownloading] = useState(false);
  const [trialEmail, setTrialEmail] = useState("");
  const [trialPlan, setTrialPlan] = useState("scale");
  const [trialDays, setTrialDays] = useState(14);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialMsg, setTrialMsg] = useState(null);

  const grantTrial = async () => {
    setTrialBusy(true); setTrialMsg(null);
    try {
      const { data } = await api.post("/admin/trial", { email: trialEmail.trim(), plan: trialPlan, days: Number(trialDays) });
      if (data.revoked) {
        setTrialMsg({ ok: true, text: `Trial revoked for ${data.email}.` });
      } else {
        setTrialMsg({ ok: true, text: `✓ ${data.plan_name} trial granted to ${data.email}${data.workspace ? ` (${data.workspace})` : ""} — ends ${fmtDateTime(data.trial_ends_at)}.` });
      }
      setTrialEmail("");
    } catch (e) {
      setTrialMsg({ ok: false, text: e?.response?.data?.detail || "Could not grant trial. Check the email and try again." });
    } finally { setTrialBusy(false); }
  };

  const params = useCallback(() => {
    const p = {};
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    return p;
  }, [fromDate, toDate]);

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/cancellations", { params: params() }).then(({ data }) => setData(data)).catch(() => setErr(true));
      api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => setErr(true));
    }
  }, [user, params]);

  const applyPreset = (pr) => {
    setPreset(pr.key);
    if (pr.days === null) { setFromDate(""); setToDate(""); }
    else { setFromDate(isoDate(new Date(Date.now() - pr.days * 864e5))); setToDate(isoDate(new Date())); }
  };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/cancellations/csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `cancellations-${toDate || "all"}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr(true); } finally { setDownloading(false); }
  };

  if (loading || user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/app" replace />;

  const total = data?.total || 0;
  const breakdown = data?.breakdown || [];
  const recent = data?.recent || [];
  const trend = data?.trend || [];
  const maxCount = breakdown.reduce((m, b) => Math.max(m, b.count), 0) || 1;
  const maxTrend = trend.reduce((m, t) => Math.max(m, t.count), 0) || 1;

  const mix = overview?.plan_mix || { free: 0, growth: 0, scale: 0 };
  const mixTotal = (mix.free || 0) + (mix.growth || 0) + (mix.scale || 0) || 1;
  const mixRows = [
    { key: "free", label: "Free", count: mix.free || 0, color: "bg-slate-400" },
    { key: "growth", label: "Growth", count: mix.growth || 0, color: "bg-blue-500" },
    { key: "scale", label: "Scale", count: mix.scale || 0, color: "bg-[#1E3A5F]" },
  ];

  return (
    <AppShell>
      <div className="border-b border-slate-200 bg-white px-6 md:px-8 py-5">
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-blue-600" /> Business Insights
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Key performance metrics and why customers cancel — to guide product and pricing decisions.</p>
      </div>

      <div className="p-6 md:p-8 max-w-4xl" data-testid="insights-page">
        {err && <p className="text-sm text-red-600">Could not load some insights.</p>}

        <h2 className="font-display text-lg font-bold text-slate-900">Business overview</h2>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <KpiCard testid="kpi-mrr" icon={PoundSterling} label="MRR (est.)"
            value={`£${(overview?.mrr ?? 0).toLocaleString("en-GB")}`}
            sub={`ARPU £${(overview?.arpu ?? 0).toLocaleString("en-GB")}/mo`} />
          <KpiCard testid="kpi-subscribers" icon={Users} label="Paying subscribers"
            value={overview?.active_paying ?? 0}
            sub={`${mix.growth || 0} Growth · ${mix.scale || 0} Scale`} />
          <KpiCard testid="kpi-total-customers" icon={Building2} label="Total customers"
            value={overview?.total_customers ?? 0}
            sub={`${overview?.new_signups_30d ?? 0} new in last 30d`} />
          <KpiCard testid="kpi-signups" icon={UserPlus} label="New signups (30d)"
            value={overview?.new_signups_30d ?? 0} />
          <KpiCard testid="kpi-churn" icon={TrendingDown} label="Churn rate (30d)"
            value={`${overview?.churn_rate_30d ?? 0}%`}
            sub={`${overview?.cancellations_30d ?? 0} cancelled`} />
        </div>

        <h2 className="mt-8 font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Layers className="h-5 w-5 text-slate-400" /> Plan mix</h2>
        <div className="mt-3 bg-white border border-slate-200 rounded-xl p-6 space-y-3" data-testid="insights-plan-mix">
          {mixRows.map((r) => (
            <div key={r.key}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700">{r.label}</span>
                <span className="font-mono text-slate-500">{r.count} · {Math.round((r.count / mixTotal) * 100)}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${r.color} transition-all`} style={{ width: `${(r.count / mixTotal) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Grant free trial */}
        <h2 className="mt-8 font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Gift className="h-5 w-5 text-slate-400" /> Grant a free trial</h2>
        <div className="mt-3 bg-white border border-slate-200 rounded-xl p-6" data-testid="grant-trial-card">
          <p className="text-sm text-slate-500 mb-4">Give a prospect full access to a paid plan for a set number of days — no payment needed. They must have signed up first. Access reverts automatically when the trial ends. Set days to 0 to revoke.</p>
          <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Account email</label>
              <input type="email" value={trialEmail} onChange={(e) => setTrialEmail(e.target.value)}
                placeholder="name@company.co.uk" data-testid="trial-email-input"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Plan</label>
              <select value={trialPlan} onChange={(e) => setTrialPlan(e.target.value)} data-testid="trial-plan-select"
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="scale">Firm</option>
                <option value="growth">Pro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Days</label>
              <input type="number" min="0" max="90" value={trialDays} onChange={(e) => setTrialDays(e.target.value)}
                data-testid="trial-days-input" className="w-20 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button onClick={grantTrial} disabled={trialBusy || !trialEmail.trim()} data-testid="grant-trial-btn"
              className="bg-[#1E3A5F] text-white rounded-lg px-5 py-2 text-sm font-semibold hover:bg-[#162c48] disabled:opacity-50 transition-colors">
              {trialBusy ? "Granting…" : "Grant trial"}
            </button>
          </div>
          {trialMsg && <p className={`mt-3 text-sm ${trialMsg.ok ? "text-green-700" : "text-red-600"}`} data-testid="trial-result-msg">{trialMsg.text}</p>}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><TrendingDown className="h-5 w-5 text-slate-400" /> Cancellation analysis</h2>
          <button onClick={exportCsv} disabled={downloading} data-testid="insights-export-csv"
            className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <Download className="h-4 w-4" /> {downloading ? "Exporting…" : "Export CSV"}
          </button>
        </div>

        <div className="mt-3 bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="insights-filter">
          <div className="flex items-center gap-1.5">
            {PRESETS.map((pr) => (
              <button key={pr.key} onClick={() => applyPreset(pr)} data-testid={`preset-${pr.key}`}
                className={`text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors ${preset === pr.key ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                {pr.label}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2 ml-auto">
            <div>
              <label className="block text-[11px] text-slate-400 font-semibold uppercase tracking-wider mb-1">From</label>
              <input type="date" value={fromDate} data-testid="filter-from"
                onChange={(e) => { setFromDate(e.target.value); setPreset(""); }}
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-[11px] text-slate-400 font-semibold uppercase tracking-wider mb-1">To</label>
              <input type="date" value={toDate} data-testid="filter-to"
                onChange={(e) => { setToDate(e.target.value); setPreset(""); }}
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        {/* Cancellations over time */}
        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="insights-trend">
          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-semibold mb-4">
            <Calendar className="h-4 w-4" /> Cancellations over time · {total} in range
          </div>
          {trend.length === 0 ? (
            <p className="text-sm text-slate-400">No cancellations in this period.</p>
          ) : (
            <div className="flex items-end gap-1.5 h-40">
              {trend.map((t) => (
                <div key={t.date} className="flex-1 flex flex-col items-center justify-end group min-w-0" data-testid="trend-bar" title={`${fmtDay(t.date)}: ${t.count}`}>
                  <span className="text-[10px] text-slate-500 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">{t.count}</span>
                  <div className="w-full rounded-t bg-blue-600 hover:bg-blue-700 transition-all" style={{ height: `${Math.max((t.count / maxTrend) * 100, 6)}%` }} />
                  <span className="text-[9px] text-slate-400 mt-1 truncate w-full text-center">{fmtDay(t.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <h2 className="mt-8 font-display text-lg font-bold text-slate-900 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-slate-400" /> Cancellation reasons</h2>
        <div className="mt-3 grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="insights-total">
            <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-semibold">
              <TrendingDown className="h-4 w-4" /> Total (in range)
            </div>
            <p className="font-display text-4xl font-bold text-[#1E3A5F] mt-2">{total}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-semibold">
              <MessageSquare className="h-4 w-4" /> Top reason
            </div>
            <p className="font-display text-xl font-bold text-[#1E3A5F] mt-2">{breakdown[0]?.reason || "—"}</p>
            {breakdown[0] && <p className="text-sm text-slate-500 mt-1">{breakdown[0].count} of {total}</p>}
          </div>
        </div>

        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-6 space-y-3" data-testid="insights-breakdown">
          {breakdown.length === 0 && <p className="text-sm text-slate-400">No cancellations recorded yet.</p>}
          {breakdown.map((b) => (
            <div key={b.reason} data-testid="breakdown-row">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700">{b.reason}</span>
                <span className="font-mono text-slate-500">{b.count} · {Math.round((b.count / total) * 100)}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${(b.count / maxCount) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>

        <h2 className="mt-8 font-display text-lg font-bold text-slate-900">Recent cancellations</h2>
        <div className="mt-3 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="insights-recent">
          {recent.length === 0 ? (
            <p className="text-sm text-slate-400 p-6">Nothing here yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Date</th>
                  <th className="text-left font-semibold px-4 py-3">Plan</th>
                  <th className="text-left font-semibold px-4 py-3">Reason</th>
                  <th className="text-left font-semibold px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recent.map((c) => (
                  <tr key={c.id} data-testid="recent-row">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDateTime(c.created_at)}</td>
                    <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">{PLAN_LABEL[c.plan] || c.plan}</span></td>
                    <td className="px-4 py-3 text-slate-800">{c.reason}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-xs truncate">{c.reason_note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default Insights;
